"use client";

import { LoginModalProps } from "@/types/auth-types";
import { slideVariants } from "@/utils/auth-utils";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLoginFlow } from "./login/useLoginFlow";
import {
  CodeStep,
  MethodStep,
  PasswordStep,
  PhoneStep,
  ResetPasswordStep,
  SignupStep,
} from "./login/LoginSteps";

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const locale = useLocale();
  const isRTL = locale === "ar";
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const flow = useLoginFlow({ isOpen, onClose });
  const { step } = flow;

  const enter = step === "phone" ? slideVariants.enterFromLeft : slideVariants.enterFromRight;
  const exit = step === "phone" ? slideVariants.exitToLeft : slideVariants.exitToRight;

  // Rendered into <body>: inside a page, maps (Leaflet z-index up to 1000) or a
  // transformed parent could otherwise end up on top of the login window.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[10050] bg-shade/50 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[10051] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-modal-title"
          >
            <div
              dir={isRTL ? "rtl" : "ltr"}
              className="najdi font-najdi relative w-full max-w-md overflow-hidden rounded-3xl bg-surface shadow-2xl shadow-ink-900/10 dark:bg-zinc-900"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                onClick={onClose}
                aria-label="Close"
                className={`absolute top-5 z-10 flex h-8 w-8 items-center justify-center rounded-full text-ink-500 transition-all duration-200 hover:bg-clay-50 hover:text-ink-900 ${
                  isRTL ? "left-5" : "right-5"
                }`}
              >
                <X size={17} />
              </button>

              {/* Back (all steps except the first) */}
              {step !== "phone" && (
                <button
                  type="button"
                  onClick={flow.goBack}
                  aria-label="back"
                  className={`absolute top-5 z-10 flex h-8 w-8 items-center justify-center rounded-full text-ink-500 transition-all duration-200 hover:bg-clay-50 hover:text-ink-900 ${
                    isRTL ? "right-5" : "left-5"
                  }`}
                >
                  <BackIcon size={17} />
                </button>
              )}

              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={step}
                  initial={enter}
                  animate={slideVariants.center}
                  exit={exit}
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  className={step === "phone" ? "" : "p-8"}
                >
                  {step === "phone" && <PhoneStep flow={flow} />}
                  {step === "method" && <MethodStep flow={flow} />}
                  {step === "password" && <PasswordStep flow={flow} />}
                  {step === "signup" && <SignupStep flow={flow} />}
                  {step === "verifySignup" && <CodeStep flow={flow} variant="verifySignup" />}
                  {step === "loginCode" && <CodeStep flow={flow} variant="loginCode" />}
                  {step === "resetPassword" && <ResetPasswordStep flow={flow} />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
